<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Playtime Tracker</span>
      <h1>Track Steam Playtime With Live Charts & Rich Summaries</h1>
      <p>
        steamplayti.me is the fastest way to monitor every minute you spend on Steam.
        Paste your profile, and we build a privacy-friendly Steam playtime tracker with daily charts,
        sticky milestones, and shareable highlights you can revisit whenever you like.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Track your Steam playtime</router-link>
        <a class="seo-secondary-link" href="#features">
          Explore tracker features →
        </a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">📈</span>
          <div>
            Daily and weekly Steam playtime graphs that instantly surface streaks, peaks, and slow weeks.
          </div>
        </li>
        <li>
          <span class="seo-highlight-icon">🔍</span>
          <div>
            Drill into any game to see lifetime minutes, recency, and trend lines without spreadsheets.
          </div>
        </li>
        <li>
          <span class="seo-highlight-icon">🛡️</span>
          <div>
            No passwords — you sign in through Steam itself, and we only read public library data. Opt out anytime.
          </div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="features">
      <h2>Steam Playtime Insights Built for Habit Tracking</h2>
      <p>
        Whether you are chasing 100% runs or guarding your free time, this Steam hour tracker keeps tabs on
        how your gaming evolves. Unlike simple counters, we preserve history and highlight shifts in your backlog.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Live Library Sync</h3>
          <p>We poll Steam automatically and refresh your profile as new sessions land. No manual exports required.</p>
        </div>
        <div class="seo-card">
          <h3>Meaningful Groupings</h3>
          <p>Toggle between 7-day, 14-day, 30-day, and all-time views to spot binge cycles or quiet seasons.</p>
        </div>
        <div class="seo-card">
          <h3>Top Games Snapshot</h3>
          <p>See what steals your hours with sortable per-game cards, store links, and quick compare notes.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Numbers You Can Act On</h2>
      <p>
        The dashboard turns raw Steam playtime data into understandable stats you can check from work, mobile, or desktop.
        It is the easiest way to answer “what did I actually play this week?” without opening the Steam client.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Historical Coverage</div>
          <div class="value">Full backlog</div>
        </div>
        <div class="seo-stat">
          <div class="label">Refresh Speed</div>
          <div class="value">Minutes</div>
        </div>
        <div class="seo-stat">
          <div class="label">Privacy</div>
          <div class="value">Public data only</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Frequently Asked About Our Steam Playtime Tracker</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>Do I have to sign in with Steam?</h3>
          <p>To start tracking your own profile, yes — you sign in through Steam once so we know the profile is yours, then confirm your e-mail. Browsing a profile that is already tracked needs no account. Your game details must be public, we never see your Steam password, and nothing gets written to your account.</p>
        </div>
        <div class="seo-faq-item">
          <h3>What if I want to keep my data private?</h3>
          <p>You can toggle your profile back to private at any time and your playtime history stops updating instantly.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I share my playtime dashboard?</h3>
          <p>Absolutely. Each tracked Steam ID lives at <code>steamplayti.me/user/&lt;your SteamID&gt;</code> and can be shared with friends or communities.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-playtime-tracker" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const META_DESCRIPTION = 'Track your Steam hours: daily playtime per game, your top games, and a history that updates every day. Sign in with Steam once — no install, no password shared.';

const META = {
  title: 'Steam Playtime Tracker & Hour Counter | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'Steam playtime tracker, Steam hour counter, track Steam hours, Steam playtime analytics, Steam gameplay tracker',
  canonical: 'https://steamplayti.me/page/steam-playtime-tracker/',
  ogTitle: 'Steam Playtime Tracker & Hour Counter',
  ogDescription: 'Build daily Steam playtime charts, see top games, and keep a living history of your gaming time on steamplayti.me.',
  ogUrl: 'https://steamplayti.me/page/steam-playtime-tracker/',
  twitterTitle: 'Steam Playtime Tracker & Hour Counter',
  twitterDescription: 'Track Steam hours with automated charts, top game rankings, and privacy-friendly syncing on steamplayti.me.',
  structuredData: pageStructuredData({
    name: 'Steam Playtime Tracker',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-playtime-tracker/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Playtime Tracker', path: 'https://steamplayti.me/page/steam-playtime-tracker/' },
    ],
  }),
};

export default {
  name: 'SteamPlaytimeTrackerPage',
  components: { SeoRelatedTools },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>

<style scoped>
code {
  background: rgba(102, 192, 244, 0.12);
  border-radius: 6px;
  padding: 2px 6px;
  color: var(--primary);
  font-family: 'Fira Code', 'Source Code Pro', monospace;
}
</style>
